/**
 * pp-swipe-fade — marca los contenedores con scroll horizontal para que el
 * CSS les desvanezca el lado hacia el que TODAVÍA hay contenido. SOLO MÓVIL.
 *
 * POR QUÉ ES GENÉRICO Y NO UNA LISTA DE CLASES. Hay 26 clases distintas con
 * `overflow-x: auto` en el tema (medido el 10-sep-2026) y salen más cada vez
 * que se agrega un carrusel. Una lista se desactualiza el primer día. Aquí se
 * detecta por COMPORTAMIENTO —el elemento se desplaza de verdad— así que lo
 * que se agregue mañana queda cubierto sin tocar este archivo.
 *
 * ── LAS TRES DECISIONES DE RENDIMIENTO ────────────────────────────────────
 *
 * 1. NADA DE requestAnimationFrame para repintar. La primera versión metía el
 *    repintado en un rAF para "coalescer". Es una trampa: en una pestaña con
 *    el render estrangulado el rAF NO CORRE, y el degradado se queda congelado
 *    en su estado inicial para siempre — lo vi pasar el 10-sep-2026 probando
 *    esto mismo. El trabajo por evento es tan barato —leer scrollLeft y quizá
 *    escribir un atributo— que se hace directo.
 *
 * 2. EL DESBORDE SE CACHEA. Leer `scrollWidth`/`clientWidth` obliga al
 *    navegador a recalcular el layout, y hacerlo en cada evento de scroll es
 *    justo el patrón que produce tirones. Se mide al observar y se refresca
 *    sólo cuando el contenedor cambia de tamaño. Durante el swipe se lee
 *    ÚNICAMENTE `scrollLeft`, que no fuerza layout.
 *
 * 3. EN CADA MUTACIÓN SE MIRAN SÓLO LOS NODOS NUEVOS. Los relacionados llegan
 *    por fetch, así que hay que reaccionar al DOM nuevo; barrer los ~750
 *    contenedores de la página en cada mutación sería reflow en cascada.
 */
(function () {
  'use strict';

  var MQ = window.matchMedia('(max-width: 749px)');
  var HOLGURA = 2; // px: bordes subpíxel no deben encender el degradado
  var vistos = new WeakSet();
  var vivos = [];  // { el, max } — el desborde cacheado

  function pintar(reg) {
    var el = reg.el;
    if (reg.max <= HOLGURA) {
      if (el.hasAttribute('data-pp-swipe')) el.removeAttribute('data-pp-swipe');
      return;
    }
    // Math.abs: en RTL scrollLeft puede ser negativo; así no hay que
    // ramificar por dirección de escritura.
    var x = Math.abs(el.scrollLeft);
    var alInicio = x <= HOLGURA;
    var alFinal = x >= reg.max - HOLGURA;
    var e = (alInicio && alFinal) ? null : (alInicio ? 'der' : (alFinal ? 'izq' : 'ambos'));
    if (e === null) {
      if (el.hasAttribute('data-pp-swipe')) el.removeAttribute('data-pp-swipe');
    } else if (el.getAttribute('data-pp-swipe') !== e) {
      el.setAttribute('data-pp-swipe', e);
    }
  }

  function medir(reg) {
    reg.max = reg.el.scrollWidth - reg.el.clientWidth;
    pintar(reg);
  }

  function observar(el) {
    var reg = { el: el, max: el.scrollWidth - el.clientWidth };
    vivos.push(reg);
    el.addEventListener('scroll', function () {
      // Guarda de caché rancia: si el scroll llegó MÁS LEJOS que el desborde
      // que teníamos medido, el contenedor creció y el ResizeObserver aún no
      // avisó — pasa cuando el navegador está estrangulando el render, donde
      // los callbacks de RO no corren (medido el 10-sep-2026). Sin esto, el
      // degradado se quedaba en "izq" a media tabla. La relectura sólo ocurre
      // en ese caso raro, así que el swipe normal sigue sin forzar layout.
      if (Math.abs(el.scrollLeft) > reg.max) {
        reg.max = el.scrollWidth - el.clientWidth;
      }
      pintar(reg);
    }, { passive: true });
    if (window.ResizeObserver) {
      new ResizeObserver(function () { medir(reg); }).observe(el);
    }
    pintar(reg);
  }

  function considerar(el) {
    if (vistos.has(el)) return;
    vistos.add(el);
    if (el.scrollWidth - el.clientWidth <= HOLGURA) return; // cedazo barato
    var ov = getComputedStyle(el).overflowX;
    if (ov === 'auto' || ov === 'scroll') observar(el);
  }

  function escanear(raiz) {
    if (!MQ.matches || !raiz.querySelectorAll) return;
    var nodos = raiz.querySelectorAll('div, ul, ol, nav, section');
    for (var i = 0; i < nodos.length; i++) considerar(nodos[i]);
  }

  function remedirTodos() {
    for (var i = 0; i < vivos.length; i++) medir(vivos[i]);
  }

  var rebote = null;
  function conRebote(fn) {
    if (rebote) clearTimeout(rebote);
    rebote = setTimeout(fn, 150);
  }

  function arrancar() {
    if (!MQ.matches) {
      var puestos = document.querySelectorAll('[data-pp-swipe]');
      for (var i = 0; i < puestos.length; i++) puestos[i].removeAttribute('data-pp-swipe');
      return;
    }
    escanear(document);

    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        var hayNuevo = false;
        for (var i = 0; i < muts.length; i++) {
          var nuevos = muts[i].addedNodes;
          for (var j = 0; j < nuevos.length; j++) {
            var n = nuevos[j];
            if (n.nodeType !== 1) continue;
            hayNuevo = true;
            considerar(n);
            escanear(n);
          }
        }
        // Contenido nuevo puede cambiar el ancho de un carrusel que ya
        // observábamos (una imagen que carga tarde, por ejemplo).
        if (hayNuevo) conRebote(remedirTodos);
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener('resize', function () { conRebote(remedirTodos); }, { passive: true });
    // Las imágenes que llegan tarde cambian el desborde sin mutar el DOM.
    window.addEventListener('load', remedirTodos);
  }

  if (MQ.addEventListener) MQ.addEventListener('change', arrancar);
  else if (MQ.addListener) MQ.addListener(arrancar);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
